/**
 * Treatment-plan approval requests in the top-bar bell (TC role test fix
 * 3): a dentist sees "A treatment-plan presentation is waiting for your
 * approval"; the coordinator who asked sees "Presentation approved". Read
 * every two minutes; one bell line per request (keyed), never a patient
 * name (the bell can show on a shared screen).
 */
import type { ApprovalFeed } from '@velora/contracts/txplan';
import { useEffect, useRef } from 'react';
import { txApi } from '../../modules/txplan/api.js';
import { useOfficeQuery } from '../office/useOfficeQuery.js';
import { usePermission } from '../session/session.js';
import { notifications, type NotificationInput } from './store.js';

export const TX_APPROVAL_POLL_MS = 2 * 60_000;

/** Bell lines for the feed (pure; tested). */
export function approvalBellItems(feed: ApprovalFeed): Array<NotificationInput & { key: string }> {
  return [
    ...feed.forMe.map((r) => ({
      key: `tx-approval:${r.id}`,
      title: 'A treatment-plan presentation is waiting for your approval',
      body: `Requested by ${r.requestedByName}.`,
      tone: 'warning' as const,
      href: `/patients/${r.patientId}/treatment?case=${r.caseId}`,
    })),
    ...feed.approved.map((r) => ({
      key: `tx-approved:${r.id}`,
      title: 'Presentation approved',
      body: `${r.resolvedByName ?? 'The dentist'} approved it. It can be printed and sent now.`,
      tone: 'success' as const,
      href: `/patients/${r.patientId}/treatment?case=${r.caseId}`,
    })),
  ];
}

export function TxApprovalNotifier() {
  const allowed = usePermission('txplan.view', { mode: 'any' });
  const q = useOfficeQuery({
    queryKey: ['tx_case', 'approval-feed'],
    queryFn: (c, s) => txApi.approvalFeed(c, s),
    enabled: allowed,
    refetchInterval: TX_APPROVAL_POLL_MS,
    staleTime: 60_000,
    retry: false,
  });
  const shown = useRef(new Set<string>());
  useEffect(() => {
    if (!q.data) return;
    const items = approvalBellItems(q.data);
    const now = new Set(items.map((i) => i.key));
    for (const key of shown.current) if (!now.has(key)) notifications.resolve(key);
    for (const i of items) if (!shown.current.has(i.key)) notifications.notify(i);
    shown.current = now;
  }, [q.data]);
  return null;
}
